import { useState } from 'react';
import { toast } from 'sonner';
import { ListChecks, Plus, Archive, Pencil } from 'lucide-react';
import { CATALOG_TASK_KINDS, PREFLIGHT_KEYS, PREFLIGHT_LABEL, type CatalogTaskKind, type PreflightKey } from '@hcc/types';
import { ApiError } from '../../lib/api';
import {
  useOnboardingCatalog,
  useCreateCatalogTask,
  useUpdateCatalogTask,
  useArchiveCatalogTask,
} from '../../hooks/useOnboardingTasks';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardHeader, CardSection } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Pill } from '../../components/ui/Pill';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonList } from '../../components/ui/Skeleton';
import { ConfirmModal } from '../../components/ui/ConfirmModal';

function slug(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
}

const inputClass = 'mt-1 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm';

const KIND_LABEL: Record<CatalogTaskKind, string> = {
  upload: 'Upload',
  todo: 'To-do',
  check: 'Check',
};

const KIND_OPTION_LABEL: Record<CatalogTaskKind, string> = {
  upload: 'A file upload',
  todo: 'A simple to-do',
  check: 'A screening check (DBS / DVLA)',
};

export function OnboardingTaskCatalog() {
  const list = useOnboardingCatalog();
  const create = useCreateCatalogTask();
  const update = useUpdateCatalogTask();
  const archive = useArchiveCatalogTask();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<CatalogTaskKind>('upload');
  const [approval, setApproval] = useState(false);
  const [preflightKey, setPreflightKey] = useState<'' | PreflightKey>('');
  const [confirm, setConfirm] = useState<{ id: string; title: string } | null>(null);

  const tasks = (list.data ?? []).filter((t) => !t.archived_at);

  function resetForm() {
    setEditingId(null);
    setTitle('');
    setDescription('');
    setKind('upload');
    setApproval(false);
    setPreflightKey('');
  }

  function startEdit(t: (typeof tasks)[number]) {
    setEditingId(t.id);
    setTitle(t.title);
    setDescription(t.description ?? '');
    setKind(t.kind);
    setApproval(t.approval_required);
    setPreflightKey(t.preflight_key ?? '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function onSave() {
    if (!title.trim()) return;
    const payload = {
      key: slug(title),
      title: title.trim(),
      description: description.trim() || undefined,
      kind,
      approval_required: approval,
      preflight_key: preflightKey || undefined,
    };
    try {
      if (editingId) {
        await update.mutateAsync({ id: editingId, ...payload });
        toast.success('Task updated');
      } else {
        await create.mutateAsync(payload);
        toast.success('Task created');
      }
      resetForm();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save task');
    }
  }

  return (
    <main className="mx-auto max-w-3xl space-y-5 px-4 py-8">
      <PageHeader
        eyebrowIcon={ListChecks}
        eyebrow="Onboarding"
        title="Task library"
        description="Reusable ad-hoc tasks: a file upload or a simple to-do. Assign them to people directly or via a role template."
      />

      <Card>
        <CardHeader title={editingId ? 'Edit task' : 'New task'} />
        <CardSection className="space-y-3">
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Upload a photo of your passport" className={inputClass} />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Description <span className="font-normal text-fg-dim">(optional)</span></span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Guidance shown to the inductee" className={inputClass} />
            <span className="mt-1 block text-xs text-fg-dim">
              Shown to the inductee. Web addresses become links, and text wrapped in double asterisks (**like this**) renders bold.
            </span>
          </label>
          <div className="flex flex-wrap gap-3">
            <label className="flex-1 text-sm">
              <span className="font-medium text-fg-muted">Type</span>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as CatalogTaskKind)}
                className={inputClass}
              >
                {CATALOG_TASK_KINDS.map((k) => (
                  <option key={k} value={k}>{KIND_OPTION_LABEL[k]}</option>
                ))}
              </select>
            </label>
            <p className="flex-1 self-end text-xs text-fg-dim">
              {kind === 'upload'
                ? 'The inductee attaches one or more files; HR can approve having seen them.'
                : kind === 'check'
                  ? 'A staged screening check: they apply (instructions = the description), HR marks it processed, then verifies the physical certificate from the checks page.'
                  : 'The inductee simply marks it done; no upload or form to fill in.'}
            </p>
          </div>
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">
              Satisfies pre-employment gate{' '}
              <span className="font-normal text-fg-dim">{kind === 'check' ? '(required for checks)' : '(optional)'}</span>
            </span>
            <select value={preflightKey} onChange={(e) => setPreflightKey(e.target.value as '' | PreflightKey)} className={inputClass}>
              <option value="">None</option>
              {PREFLIGHT_KEYS.map((k) => (
                <option key={k} value={k}>{PREFLIGHT_LABEL[k]}</option>
              ))}
            </select>
            {preflightKey ? (
              <span className="mt-1 block text-xs text-fg-dim">
                Approving this task auto-completes the {PREFLIGHT_LABEL[preflightKey]} gate on the person and locks it. Gate tasks always require sign-off.
              </span>
            ) : null}
          </label>
          <label className="flex items-center gap-2 text-sm text-fg">
            <input type="checkbox" checked={approval || !!preflightKey} disabled={!!preflightKey} onChange={(e) => setApproval(e.target.checked)} />
            Needs HR sign-off after submission
          </label>
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              iconLeft={editingId ? undefined : <Plus className="h-4 w-4" />}
              onClick={onSave}
              loading={create.isPending || update.isPending}
              disabled={!title.trim() || (kind === 'check' && !preflightKey)}
            >
              {editingId ? 'Save changes' : 'Create task'}
            </Button>
            {editingId ? (
              <Button variant="ghost" size="sm" onClick={resetForm}>
                Cancel
              </Button>
            ) : null}
          </div>
        </CardSection>
      </Card>

      {list.isLoading ? (
        <SkeletonList rows={3} />
      ) : tasks.length === 0 ? (
        <EmptyState icon={<ListChecks className="h-6 w-6" />} title="No tasks yet" description="Create a reusable task above." />
      ) : (
        <div className="space-y-3">
          {tasks.map((t) => (
            <Card key={t.id}>
              <CardSection className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-medium text-fg">{t.title}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    <Pill tone="neutral" size="sm">{KIND_LABEL[t.kind]}</Pill>
                    {t.approval_required ? <Pill tone="info" size="sm">Sign-off</Pill> : null}
                    {t.preflight_key ? <Pill tone="accent" size="sm">Gate: {PREFLIGHT_LABEL[t.preflight_key]}</Pill> : null}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button variant="secondary" size="sm" iconLeft={<Pencil className="h-4 w-4" />} onClick={() => startEdit(t)}>
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" iconLeft={<Archive className="h-4 w-4" />} onClick={() => setConfirm({ id: t.id, title: t.title })}>
                    Archive
                  </Button>
                </div>
              </CardSection>
            </Card>
          ))}
        </div>
      )}

      <ConfirmModal
        open={!!confirm}
        title="Archive task?"
        message={confirm ? `"${confirm.title}" will be hidden from the library. People already assigned keep it.` : ''}
        tone="warning"
        confirmLabel="Archive"
        onConfirm={async () => {
          if (!confirm) return;
          try {
            await archive.mutateAsync(confirm.id);
            toast.success('Task archived');
          } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not archive');
          }
          setConfirm(null);
        }}
        onClose={() => setConfirm(null)}
      />
    </main>
  );
}
